// The client half of the E key. Placed entities (trees, rocks, fish) use
// "hold E": ask the server to start, show the progress it confirms, cancel on release. Tile entities and bags use
// "press E" to open them. The server makes every decision.
import { GameConstants, HarvestPhase } from '../../../shared/src/constants';
import { Harvest, TileEntities, tileEntityDef, type TileEntity } from '../../../shared/src/rules/world';
import type { ClientSession } from './session';
import { content } from './content';
import type { PlacedEntity } from '../../../shared/src/entityGeometry';
import type { LootBag } from './world';
import { LifeState, Rekindle } from '../../../shared/src/rules/buffs';

enum Phase { Idle, Pending, Active }

/** What pressing E asked to open this frame. */
export interface OpenRequest { station: TileEntity | null; bag: LootBag | null }

export class InteractionController {
  /** Client reach for bags (the server allows a little more). */
  static readonly bagReachPixels = 22;

  private phase = Phase.Idle;
  private tile: [number, number] = [0, 0];
  private startTime = 0;
  private duration = 0;
  private pendingSince = 0;
  private rejected: [number, number] | null = null;

  /** The harvestable tile E would act on now. */
  hover: [number, number] | null = null;
  hoverResource: PlacedEntity | null = null;
  activeResourceId = "";
  /** The tile entity E would use now. */
  hoverEntity: TileEntity | null = null;
  /** The bag E would open now. */
  hoverBag: LootBag | null = null;
  /** A ghost's own corpse, which hold E rekindles (its tile is `hover`). */
  hoverCorpse = false;
  private activeCorpse = false;

  get activeTile(): [number, number] | null { return this.phase === Phase.Active ? this.tile : null; }
  get isBusy(): boolean { return this.phase !== Phase.Idle; }

  reset(): void {
    this.phase = Phase.Idle;
    this.rejected = null;
    this.hover = null;
    this.hoverResource = null;
    this.activeResourceId = "";
    this.hoverEntity = null;
    this.hoverBag = null;
    this.hoverCorpse = false;
  }

  progress(now: number): number | null {
    return this.phase === Phase.Active ? Math.min(1, Math.max(0, (now - this.startTime) / Math.max(0.001, this.duration))) : null;
  }

  /** Updates targeting and hold-to-harvest; returns what to open when E was pressed on a station or bag. */
  update(session: ClientSession, held: boolean, pressed: boolean, blocked: boolean): OpenRequest | null {
    const now = session.time;
    const local = session.local;
    this.hover = null;
    this.hoverResource = null;
    this.hoverEntity = null;
    this.hoverBag = null;
    this.hoverCorpse = false;
    if (!blocked) this.pickTarget(local.x, local.y, local.state.facing, session);

    if (!held || blocked) {
      if (this.phase !== Phase.Idle) session.sendInteractCancel();
      this.phase = Phase.Idle;
      if (!held) this.rejected = null;
      return null;
    }
    if (pressed) {
      this.rejected = null;
      if (this.phase === Phase.Idle && this.hoverEntity) return { station: this.hoverEntity, bag: null };
      if (this.phase === Phase.Idle && this.hoverBag) return { station: null, bag: this.hoverBag };
    }
    if (this.phase === Phase.Pending && now - this.pendingSince > 2) this.phase = Phase.Idle; // lost reply: allow a retry

    const target = this.hover;
    const same = (a: [number, number] | null, b: [number, number] | null) => !!a && !!b && a[0] === b[0] && a[1] === b[1];
    if (target && !same(target, this.rejected) && (this.phase === Phase.Idle || !same(target, this.tile))) {
      this.tile = target;
      this.phase = Phase.Pending;
      this.pendingSince = now;
      this.activeResourceId = (this.hoverResource as PlacedEntity | null)?.id ?? "";
      this.activeCorpse = this.hoverCorpse;
      if (this.activeCorpse) session.sendRekindle();
      else if (this.activeResourceId) session.sendGatherEntity(this.activeResourceId);
      else session.sendInteractStart(target[0], target[1]);
    }
    return null;
  }

  private pickTarget(x: number, y: number, facing: number, session: ClientSession): void {
    const world = session.world;
    // The dead act on nothing; a ghost only on its own corpse.
    const life = session.life?.state ?? LifeState.Alive;
    if (life === LifeState.Dead) return;
    if (life === LifeState.Ghost) {
      const corpse = session.corpses.get(session.local.entityId);
      if (corpse && Math.hypot(x - corpse.x, y - corpse.y) <= Rekindle.reachPixels) {
        this.hover = [Math.floor(corpse.x / GameConstants.tileSize), Math.floor((corpse.y - 1) / GameConstants.tileSize)];
        this.hoverCorpse = true;
      }
      return;
    }
    const [ax, ay] = Harvest.aimPoint(x, y, facing);
    let resource: PlacedEntity | null = null, resourceScore = Infinity;
    for (const e of [...world.map.placedEntities.values()].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0)) {
      if (e.depleted || !content().entity(e.definition)?.gather) continue;
      const cx = (e.x + 0.5) * GameConstants.tileSize, cy = (e.y + 0.5) * GameConstants.tileSize;
      if (Math.hypot(x - cx, y - cy) > Harvest.clientReachPixels) continue;
      const score = Math.hypot(ax - cx, ay - cy);
      if (score < resourceScore) { resource = e; resourceScore = score; }
    }
    const object: [number, number] | null = resource ? [resource.x, resource.y] : null;
    const tile = object;
    const tileScore = tile ? Math.hypot(ax - (tile[0] + 0.5) * GameConstants.tileSize, ay - (tile[1] + 0.5) * GameConstants.tileSize) : Infinity;

    let best: TileEntity | null = null, entityScore = Infinity;
    for (const e of world.tileEntities.values()) {
      if (!tileEntityDef(e.type)?.verb || !TileEntities.canInteract(e, x, y, facing)) continue;
      const score = TileEntities.distanceTo(e, ax, ay);
      if (score < entityScore) { entityScore = score; best = e; }
    }
    let bestBag: LootBag | null = null, bagScore = Infinity;
    for (const b of world.bags.values()) {
      if (Math.hypot(b.x - x, b.y - y) > InteractionController.bagReachPixels) continue;
      const score = Math.hypot(ax - b.x, ay - b.y);
      if (score < bagScore) { bagScore = score; bestBag = b; }
    }
    const min = Math.min(tileScore, entityScore, bagScore);
    if (min === Infinity) return;
    if (bestBag && bagScore === min) this.hoverBag = bestBag;
    else if (best && entityScore === min) this.hoverEntity = best;
    else {
      this.hover = tile;
      if (tile === object) this.hoverResource = resource;
    }
  }

  onServerState(phase: HarvestPhase, x: number, y: number, durationTicks: number, now: number): void {
    if (this.phase === Phase.Idle || x !== this.tile[0] || y !== this.tile[1]) return;
    switch (phase) {
      case HarvestPhase.Started:
        this.phase = Phase.Active;
        this.startTime = now;
        this.duration = durationTicks / GameConstants.tickRate;
        break;
      case HarvestPhase.Completed:
        this.phase = Phase.Idle;
        break;
      case HarvestPhase.Cancelled:
        this.phase = Phase.Idle;
        this.rejected = [x, y]; // don't spam the server while E stays held
        break;
    }
  }
}
